import type { BusinessConnectionType } from '@libs/enums';
import {
  Characters,
  type ItemAttributes,
  ItemName,
  type ItemNames,
  Locale,
  ValueSet,
  WithBlankSearch,
  type WithBlankSearchObject,
  WithExclusion,
  type WithExclusionObject,
} from '@ps-smart-cp/ppfw-client';

interface Schema {
  // 取引先区分
  businessConnectionClass: Set<BusinessConnectionType>;

  // 取引先マスタコード
  businessConnectionMstCode: string;

  // 取引先マスタコード(抽出条件)
  businessConnectionMstCodeRange: WithExclusionObject;

  // 取引先(表示用)
  businessConnection: string;

  // 購入先補足名称
  supplierSuppName: string;

  // 購入先補足コード範囲(抽出条件)
  supplierSuppCodeRange: WithExclusionObject;

  // 貸出先補足コード範囲(抽出条件)
  lentPartySuppCodeRange: WithExclusionObject;

  // 購入先補足名称(抽出条件)
  supplierSuppNameCondition: WithBlankSearchObject;

  // 貸出先補足名称(抽出条件)
  lentPartySuppNameCondition: WithBlankSearchObject;

  // 購入先(表示用)
  supplier: string;

  // 購入先コード
  supplierCode: string;

  // 購入先名称
  supplierName: string;

  // 貸出先(表示用)
  lentParty: string;

  // 貸出先コード
  lentPartyCode: string;

  // 貸出先名称
  lentPartyName: string;

  // 貸出先補足名称
  lentPartySuppName: string;
}

interface Record {
  businessConnectionMstCodeRange: string;
  isBusinessConnectionMstCodeRangeExcluded: boolean;
  businessConnectionClass: string[];
  businessConnection: string;
  supplierSuppName: string;
  supplierSuppNameCondition: string;
  isSupplierSuppNameConditionExcluded: boolean;
  isSupplierSuppNameBlankOnly: boolean;
  lentPartySuppNameCondition: string;
  isLentPartySuppNameConditionExcluded: boolean;
  isLentPartySuppNameBlankOnly: boolean;
  supplierSuppCodeRange: string;
  isSupplierSuppCodeRangeExcluded: boolean;
  lentPartySuppCodeRange: string;
  isLentPartySuppCodeRangeExcluded: boolean;
  supplier: string;
  supplierCode: string;
  supplierName: string;
  lentParty: string;
  lentPartyCode: string;
  lentPartyName: string;
  lentPartySuppName: string;
}

const attributes = {
  businessConnectionClass: ValueSet.new(true),
  businessConnectionMstCode: Characters.preset.halfWidth({ minLen: 8, maxLen: 8, required: true }),
  businessConnectionMstCodeRange: WithExclusion.of(Characters.preset.halfWidth({ minLen: 0, maxLen: 250 })),
  businessConnection: Characters.preset.halfWidth({ minLen: 8, maxLen: 8 }),
  supplierSuppName: Characters.preset.freeFormat(),
  supplierSuppNameCondition: WithBlankSearch.of(Characters.preset.freeFormat()),
  lentPartySuppNameCondition: WithBlankSearch.of(Characters.preset.freeFormat()),
  supplierSuppCodeRange: WithExclusion.of(Characters.preset.halfWidth({ minLen: 0, maxLen: 250 })),
  lentPartySuppCodeRange: WithExclusion.of(Characters.preset.halfWidth({ minLen: 0, maxLen: 250 })),
  supplierCode: Characters.preset.freeFormat(),
  supplierName: Characters.preset.freeFormat(),
  lentPartyCode: Characters.preset.freeFormat(),
  lentPartyName: Characters.preset.freeFormat(),
  lentPartySuppName: Characters.preset.freeFormat(),
} satisfies ItemAttributes<Schema>;

const names = {
  businessConnectionClass: ItemName.of([Locale.ja, '取引先区分']),
  businessConnectionMstCode: ItemName.of([Locale.ja, '取引先']),
  businessConnectionMstCodeRange: ItemName.of([Locale.ja, '取引先範囲']),
  businessConnection: ItemName.of([Locale.ja, '取引先']),
  supplierSuppName: ItemName.of([Locale.ja, '購入先補足名称']),
  supplierSuppNameCondition: ItemName.of([Locale.ja, '購入先補足名称']),
  lentPartySuppNameCondition: ItemName.of([Locale.ja, '貸出先補足名称']),
  supplierSuppCodeRange: ItemName.of([Locale.ja, '購入先範囲']),
  lentPartySuppCodeRange: ItemName.of([Locale.ja, '貸出先範囲']),
  supplier: ItemName.of([Locale.ja, '購入先']),
  supplierCode: ItemName.of([Locale.ja, '購入先コード']),
  supplierName: ItemName.of([Locale.ja, '購入先名称']),
  lentParty: ItemName.of([Locale.ja, '貸出先']),
  lentPartyCode: ItemName.of([Locale.ja, '貸出先コード']),
  lentPartyName: ItemName.of([Locale.ja, '貸出先名称']),
  lentPartySuppName: ItemName.of([Locale.ja, '貸出先補足名称']),
} satisfies ItemNames<Schema>;

const labelNames = {
  businessConnection: ItemName.of([Locale.ja, '取引先']),
  businessConnectionClassSupplier: ItemName.of([Locale.ja, '取引先区分(購入先)']),
  businessConnectionClassLentParty: ItemName.of([Locale.ja, '取引先区分(貸出先)']),
  businessConnectionClassPayee: ItemName.of([Locale.ja, '取引先区分(支払先)']),
  businessConnectionClassBuyer: ItemName.of([Locale.ja, '取引先区分(売却先)']),
};

const businessConnectionMst = {
  names,
  attributes,
  labelNames,
};

interface BusinessConnectionMst {
  schema: Schema;
  record: Record;
}

export { businessConnectionMst };
export type { BusinessConnectionMst };
